import React, { useState } from 'react';

import { Autocomplete, Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import type { LensDocument, LensDocumentData, Param } from './types';
import type { BoardEditor } from './useBoardEdit';

type Kind = 'measures' | 'columns' | 'value' | 'sql';

const KINDS: Array<[Kind, string, string]> = [
  ['measures', 'Measure switch', 'Readers swap the measure that blocks written with $name draw.'],
  ['columns', 'Split switch', 'Readers swap the column that blocks split by $name.'],
  ['value', 'Number input', 'A number readers set, read in measures and SQL as $name.'],
  ['sql', 'SQL input', 'A value readers pick, bound into dataset SQL as @name.'],
];

const kindOf = (param: Param): Kind => ('measures' in param ? 'measures' : 'columns' in param ? 'columns' : 'sql' in param ? 'sql' : 'value');

type Choice = { id: string; label: string };

function ParamForm({ id, param, saved, measures, columns, editor, onDone }: { id: string; param: Param; saved: boolean; measures: Array<Choice>; columns: Array<Choice>; editor: BoardEditor; onDone?: () => void }) {
  const [draft, setDraft] = useState<Record<string, unknown>>(param as Record<string, unknown>);
  const kind = kindOf(param);
  const set = (patch: Record<string, unknown>) => setDraft({ ...draft, ...patch });
  const list = (key: string) => (Array.isArray(draft[key]) ? (draft[key] as Array<string>) : []);
  const number = (key: string) => (typeof draft[key] === 'number' ? String(draft[key]) : '');
  const numeric = (text: string) => (text.trim() === '' || !Number.isFinite(Number(text)) ? undefined : Number(text));
  const picker = (key: string, label: string, choices: Array<Choice>) => (
    <Autocomplete
      getOptionLabel={(option) => choices.find((choice) => choice.id === option)?.label ?? option}
      multiple
      onChange={(_, next) => set({ [key]: next })}
      options={choices.map((choice) => choice.id)}
      renderInput={(params) => <TextField {...params} label={label} size="small" />}
      size="small"
      value={list(key)}
    />
  );
  const ready = kind === 'measures' ? list('measures').length > 1 : kind === 'columns' ? list('columns').length > 1 : kind === 'sql' ? Boolean(draft.sql) && list('options').length > 0 : typeof draft.value === 'number';
  return (
    <Stack spacing={1.25} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
      <TextField label="Label" onChange={(event) => set({ label: event.target.value || undefined })} size="small" value={typeof draft.label === 'string' ? draft.label : ''} />
      {kind === 'measures' ? picker('measures', 'Measures to switch between', measures) : null}
      {kind === 'columns' ? picker('columns', 'Columns to split by', columns) : null}
      {kind === 'value' ? (
        <Stack direction="row" spacing={1}>
          {(['value', 'min', 'max', 'step'] as const).map((key) => (
            <TextField key={key} label={key === 'value' ? 'Starts at' : key === 'min' ? 'Lowest' : key === 'max' ? 'Highest' : 'Step'} onChange={(event) => set({ [key]: numeric(event.target.value) })} size="small" value={number(key)} />
          ))}
        </Stack>
      ) : null}
      {kind === 'sql' ? (
        <>
          <TextField helperText="The name the dataset SQL binds, e.g. region for @region." label="Bound as" onChange={(event) => set({ sql: event.target.value })} size="small" value={typeof draft.sql === 'string' ? draft.sql : ''} />
          <Autocomplete freeSolo multiple onChange={(_, next) => set({ options: next.map(String) })} options={[]} renderInput={(params) => <TextField {...params} helperText="Type a value and press Enter." label="Choices" size="small" />} size="small" value={list('options')} />
          <Autocomplete multiple onChange={(_, next) => set({ value: next.map(String) })} options={list('options')} renderInput={(params) => <TextField {...params} label="Starts on" size="small" />} size="small" value={list('value')} />
          <FormControlLabel control={<Switch checked={draft.multiple === true} onChange={(event) => set({ multiple: event.target.checked || undefined })} size="small" />} label="Readers may pick several" />
        </>
      ) : null}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {saved ? (
          <Button color="error" disabled={editor.pending} onClick={() => editor.apply({ op: 'param', id, def: null })} size="small" variant="text">
            Remove
          </Button>
        ) : null}
        {onDone ? (
          <Button onClick={onDone} size="small" variant="text">
            Close
          </Button>
        ) : null}
        <Button
          disabled={editor.pending || !ready}
          onClick={() => {
            editor.apply({ op: 'param', id, def: Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, value === undefined ? null : value])) });
            onDone?.();
          }}
          size="small"
          variant="contained"
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

export default function ParamsEditor({ document, data, editor, only, exclude, onDone }: { document: LensDocument; data?: LensDocumentData; editor: BoardEditor; only?: string; exclude?: Array<string>; onDone?: () => void }) {
  const fields = useGetReportingLensFields(data?.lensId ?? '', data?.lensVersion);
  const [open, setOpen] = useState<string>();
  const [adding, setAdding] = useState<Kind>();
  const [name, setName] = useState('');
  const measures: Array<Choice> = [
    ...(fields.data?.measures ?? []).map((measure) => ({ id: measure.id, label: measure.label })),
    ...(fields.data?.datasets ?? []).flatMap((dataset) => dataset.certified.map((measure) => ({ id: measure.id, label: measure.label }))),
  ].filter((choice, index, all) => all.findIndex((other) => other.id === choice.id) === index);
  const columns: Array<Choice> = [...new Map((fields.data?.datasets ?? []).flatMap((dataset) => dataset.columns.filter((column) => column.kind === 'dimension').map((column) => [column.name, { id: column.name, label: column.label }] as const))).values()];
  const id = name.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  const fresh = (kind: Kind): Param => (kind === 'measures' ? { measures: [] } : kind === 'columns' ? { columns: [] } : kind === 'sql' ? { sql: id, options: [] } : { value: 0 });
  if (only) return document.params[only] ? <ParamForm columns={columns} editor={editor} id={only} measures={measures} onDone={onDone} param={document.params[only]} saved /> : null;
  return (
    <Stack data-lens-params spacing={1}>
      {Object.entries(document.params).filter(([key]) => !exclude?.includes(key)).map(([key, param]) => (
        <Stack key={key} spacing={1}>
          <Button aria-expanded={open === key} onClick={() => setOpen(open === key ? undefined : key)} size="small" sx={{ color: 'text.primary', justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }} variant="text">
            <Stack>
              <Typography variant="body2">{('label' in param && param.label) || key}</Typography>
              <Typography color="text.secondary" variant="caption">{`${KINDS.find(([kind]) => kind === kindOf(param))?.[1]} · $${key}`}</Typography>
            </Stack>
          </Button>
          {open === key ? <ParamForm columns={columns} editor={editor} id={key} measures={measures} onDone={() => setOpen(undefined)} param={param} saved /> : null}
        </Stack>
      ))}
      {adding ? (
        <Stack spacing={1}>
          <TextField helperText={KINDS.find(([kind]) => kind === adding)?.[2]} label="Name" onChange={(event) => setName(event.target.value)} placeholder="metric" size="small" value={name} />
          {id && !document.params[id] ? <ParamForm columns={columns} editor={editor} id={id} key={`${adding}:${id}`} measures={measures} onDone={() => setAdding(undefined)} param={fresh(adding)} saved={false} /> : null}
          <Box>
            <Button onClick={() => setAdding(undefined)} size="small" variant="text">
              Cancel
            </Button>
          </Box>
        </Stack>
      ) : (
        <TextField
          label="Add a switch or input"
          onChange={(event) => {
            setAdding(event.target.value as Kind);
            setName('');
          }}
          select
          size="small"
          value=""
        >
          {KINDS.map(([kind, label, help]) => (
            <MenuItem key={kind} value={kind}>
              <Stack>
                <Typography variant="body2">{label}</Typography>
                <Typography color="text.secondary" variant="caption">
                  {help}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      )}
      {Object.keys(document.params).length ? null : (
        <Typography color="text.secondary" variant="caption">
          No switches yet.
        </Typography>
      )}
    </Stack>
  );
}
